import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, Container, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import Footer from '../home/Footer';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import './Login.css';
import AuthService from '../services/AuthService';
import CustomButton from '../customComponents/CustomButton';
import CustomFormAction from '../customComponents/CustomFormAction';

const loginSchema = z.object({
    emailAddress: z.string().min(1, "Email Address is required").email("Invalid email format"),
    password: z.string().min(6, "Password must be at least 6 characters"),

});

type LoginFormInputs = z.infer<typeof loginSchema>;

const Login: React.FC = () => {
    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormInputs>({
        resolver: zodResolver(loginSchema),
    });

    const [loading, setLoading] = useState(false);
    const [goToConfirm, setGoToConfirm] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useEffect(() => {
        if (!AuthService.isTokenExpired()) {
            navigate('/')
        }
        if (AuthService.isTokenExpired()) {
            AuthService.logout();
        }
    });


    const goHome = () => {
        navigate('/home');
    }

    const handleLogin = async (data: LoginFormInputs) => {
        setLoading(true);

        try {
            const response = await AuthService.login({
                emailAddress: data.emailAddress,
                password: data.password,
                rememberClient: true,
            });

            console.log('response', response.success);

            if (response.success) {
                enqueueSnackbar('Welcome', { variant: 'success' });
                navigate('/');
            }
        } catch (error) {

            let errorMessage = typeof error === 'string' ? error : 'Login failed. Please try again.';

            if (errorMessage === 'User is not confirmed.') {
                setGoToConfirm(true);
            }

            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <Container component="main" maxWidth="xs">
                <Paper className="login-paper" elevation={3}>
                    <Box className="logo-box" >
                        <img onClick={goHome} style={{ cursor: 'pointer' }} src="logo.png" alt="Logo" className="login-logo" />
                    </Box>
                    <Typography component="h1" variant="h5" className="login-title">
                        Login to System
                    </Typography>
                    <form onSubmit={handleSubmit(handleLogin)} noValidate>
                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                id="emailAddress"
                                label="Email"
                                InputLabelProps={{ shrink: true }}
                                autoComplete="username"
                                {...register("emailAddress")}
                                error={!!errors.emailAddress}
                                helperText={errors.emailAddress?.message}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Password"
                                InputLabelProps={{ shrink: true }}
                                type="password"
                                autoComplete="current-password"
                                {...register("password")}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                            />
                        </Box>

                        <CustomFormAction>
                            <CustomButton
                                lable={loading ? 'Logging in...' : 'Login'}
                                type='submit'
                                disabled={loading}
                                infoContent={`Login in application`}
                                size='medium'
                            />
                        </CustomFormAction>

                        <Box mt={2} textAlign="center">
                            <Typography variant="body2">
                                Don't have an account? <a href="/register" style={{ textDecoration: "none", color: "#1976d2" }}>Sign up here</a>
                            </Typography>
                        </Box>

                         <Box mt={2} textAlign="center">
                            <Typography variant="body2">
                                Forgot your password? <a href="/resetpassword" style={{ textDecoration: "none", color: "#1976d2" }}>Reset it here</a>
                            </Typography>
                        </Box>

                        {goToConfirm && <Box mt={2} textAlign="center">
                            <Typography variant="body2">
                                <a href="/confirmregistration" className="blink-text" style={{ textDecoration: "none", color: "red" }}>Please confirm by your email here!</a>
                            </Typography>
                        </Box>}
                    </form>
                </Paper>
            </Container>
            <div className="footer-login-container" >
                <Footer />
            </div>
        </div>
    );
};

export default Login;
